import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import AppShell from "../shell/AppShell.jsx";
import { useHierarchyTree, findPlc } from "./useHierarchyTree.js";
import DetailDrawer from "../components/DetailDrawer.jsx";
import "./orgchart.css";
import "./hierarchy.css";
import "./superAgentsOverview.css";
import "./superAgentLineOrgChart.css";
import "./plcDetail.css";
import "../pages/states.css";

import PageSkeleton from "../components/Skeleton.jsx";
const CRUMB_BASE = ["Backoffice", "SuperAdmin", "Super Agents"];
const DRAWER_TITLE_ID = "plc-drawer-title";

function matchesQuery(query, ...fields) {
  if (!query) return true;
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return fields.some((field) => String(field ?? "").toLowerCase().includes(needle));
}

/** Sums Worker/Invite counts for one PLC's Agents - derived from the already-loaded tree, nothing invented. */
function plcTotals(plc) {
  const agents = plc.agents ?? [];
  return agents.reduce(
    (acc, agent) => ({
      agentCount: acc.agentCount + 1,
      workerCount: acc.workerCount + (agent.workerCount ?? 0),
      inviteCount: acc.inviteCount + (agent.inviteCount ?? 0),
    }),
    { agentCount: 0, workerCount: 0, inviteCount: 0 }
  );
}

/**
 * /backoffice/super-agents/:superAgentId/plcs/:plcId - the PLC/บมจ home page
 * one level below the Super Agent org chart (P086). Answers "which Agents
 * does this PLC own, and how much are they carrying" for the PLC the
 * SuperAdmin just opened from that org chart's PLC drawer.
 *
 * Reuses the P086R drawer pattern exactly: the Agent grid below is the
 * page's full-width primary work area at rest, and Agent detail only opens
 * as a slide-over drawer after a card is clicked - never a permanent side
 * column. Node/row selection stays page-local state; only :superAgentId and
 * :plcId are route params.
 *
 * Contact, Safety/abnormal, e-KYC, Worker Group, and Agent SLA/performance
 * have no backing API yet, so every zone that would show them renders an
 * explicit reserved state instead of invented data. Invite Card stays
 * Agent-only: this page shows the same read-only policy note as the org
 * chart page but no send-invite action anywhere.
 *
 * P088: the Agent drawer's action link now goes to the real Agent detail
 * page at .../agents/:agentId (SuperAgentAgentDetailPage.jsx) instead of a
 * disabled "next packet" button.
 */
export default function SuperAgentPlcDetailPage() {
  const { superAgentId, plcId } = useParams();
  const { status, data: tree, error } = useHierarchyTree("worker-intake");
  const [query, setQuery] = useState("");
  const [selectedAgentId, setSelectedAgentId] = useState(null);

  if (status === "loading") {
    return (
      <AppShell crumbs={CRUMB_BASE}>
        <PageSkeleton variant="table" toolbar={false} label="กำลังโหลดข้อมูล PLC / บมจ..." />
      </AppShell>
    );
  }

  if (status === "error") {
    return (
      <AppShell crumbs={CRUMB_BASE}>
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">ไม่สามารถโหลดข้อมูล PLC / บมจ ได้</h1>
          <p className="wx-state-body">
            {error?.status === 401
              ? "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่"
              : "กรุณาลองรีเฟรชหน้านี้อีกครั้ง หากยังพบปัญหากรุณาติดต่อฝ่ายปฏิบัติการ"}
          </p>
        </div>
      </AppShell>
    );
  }

  const { superAgent, plc } = findPlc(tree, superAgentId, plcId);

  if (!superAgent) {
    return (
      <AppShell crumbs={CRUMB_BASE}>
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">ไม่พบ Super Agent นี้</h1>
          <p className="wx-state-body">
            Super Agent ที่คุณเปิดอาจถูกย้าย ปิดใช้งาน หรือไม่อยู่ในสิทธิ์การเข้าถึงของคุณ กรุณากลับไปเลือก Super Agent
            จากรายการ
          </p>
          <p className="wx-state-reference">Super Agent ID: {superAgentId}</p>
          <p className="wx-state-contact">
            <Link className="wx-breadcrumb-link" to="/backoffice/super-agents">
              กลับไปหน้ารายชื่อ Super Agent
            </Link>
          </p>
        </div>
      </AppShell>
    );
  }

  if (!plc) {
    const superAgentCrumbs = [...CRUMB_BASE, superAgent.organizationName];
    return (
      <AppShell crumbs={superAgentCrumbs}>
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">ไม่พบ PLC / บมจ นี้</h1>
          <p className="wx-state-body">
            PLC / บมจ ที่คุณเปิดอาจถูกย้าย ปิดใช้งาน หรือไม่อยู่ภายใต้ {superAgent.organizationName} กรุณากลับไปเลือก
            PLC / บมจ จากผังสายงาน
          </p>
          <p className="wx-state-reference">PLC ID: {plcId}</p>
          <p className="wx-state-contact">
            <Link className="wx-breadcrumb-link" to={`/backoffice/super-agents/${superAgentId}`}>
              กลับไปผังสายงาน {superAgent.organizationName}
            </Link>
          </p>
        </div>
      </AppShell>
    );
  }

  const crumbs = [...CRUMB_BASE, superAgent.organizationName, plc.organizationName];
  const allAgents = plc.agents ?? [];
  const totals = plcTotals(plc);

  const filteredAgents = allAgents.filter((agent) => matchesQuery(query, agent.displayName, agent.agentCode));
  const selectedAgent = allAgents.find((agent) => agent.agentId === selectedAgentId) ?? null;

  return (
    <AppShell crumbs={crumbs}>
      <div className="wx-sa-page">
        <div className="wx-sa-toolbar">
          <label className="wx-sa-search">
            <span className="wx-visually-hidden">Search agent, worker group, code</span>
            <input
              type="search"
              placeholder="Search agent, worker group, code..."
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
          <div className="wx-sa-toolbar-actions">
            <button type="button" className="wx-button wx-button--ghost" disabled title="ยังไม่เปิดใช้งาน">
              Export
            </button>
            <button
              type="button"
              className="wx-button wx-button--primary"
              disabled
              title="ยังไม่เปิดใช้งาน"
            >
              Create Change Request
            </button>
          </div>
        </div>

        <header className="wx-plc-head-row">
          <div className="wx-plc-head-text wx-sa-head">
            <h1 className="wx-sa-title">หน้า PLC / บมจ</h1>
            <p className="wx-sa-subtitle">
              <strong>{plc.organizationName}</strong> อยู่ภายใต้ {superAgent.organizationName} ดูแล Agent และแรงงาน
              หลายกลุ่มในสายงานนี้
            </p>
          </div>
          <span className="wx-badge wx-badge--info wx-plc-scope-pill">Scope: {plc.organizationCode}</span>
        </header>

        <div className="wx-sa-kpis">
          <KpiCard label="AGENTS" value={totals.agentCount} />
          <KpiCard label="WORKERS" value={totals.workerCount} />
          <KpiCard label="INVITES" value={totals.inviteCount} />
        </div>

        <div className="wx-loc-policy-note">
          <strong>Invite card ส่งจาก Agent เท่านั้น</strong>
          <span>PLC / บมจ ดูสถานะการเชิญของ Agent ได้ แต่ส่งแทน Agent ไม่ได้</span>
        </div>

        <section className="wx-panel wx-loc-chart-panel">
          <div className="wx-loc-panel-head">
            <h2 className="wx-panel-title">Agent ภายใต้ {plc.organizationName}</h2>
            <span className="wx-loc-panel-scope">{filteredAgents.length} รายการ</span>
          </div>

          {allAgents.length === 0 ? (
            <p className="wx-empty-list wx-plc-panel-body">ยังไม่มี Agent ภายใต้ PLC / บมจ นี้</p>
          ) : filteredAgents.length === 0 ? (
            <p className="wx-empty-list wx-plc-panel-body">ไม่พบผลลัพธ์ที่ตรงกับคำค้นหา "{query}"</p>
          ) : (
            <div className="wx-plc-agent-grid">
              {filteredAgents.map((agent) => {
                const isSelected = selectedAgentId === agent.agentId;
                return (
                  <button
                    type="button"
                    key={agent.agentId}
                    className={`wx-plc-agent-card${isSelected ? " wx-plc-agent-card--selected" : ""}`}
                    onClick={() => setSelectedAgentId(agent.agentId)}
                  >
                    <div className="wx-plc-agent-card-title">{agent.displayName}</div>
                    <div className="wx-plc-agent-card-code">{agent.agentCode}</div>
                    <div className="wx-node-card-metrics">
                      <div className="wx-node-card-metric">
                        <span className="wx-node-card-metric-value">{agent.workerCount ?? 0}</span>
                        <span className="wx-node-card-metric-label">แรงงาน</span>
                      </div>
                      <div className="wx-node-card-metric">
                        <span className="wx-node-card-metric-value">{agent.inviteCount ?? 0}</span>
                        <span className="wx-node-card-metric-label">คำเชิญ</span>
                      </div>
                    </div>
                    {/* Three chips - "e-KYC: ยังไม่เปิดใช้งาน",
                        "ความเสี่ยง: …Safety API", "ผู้ติดต่อ: …Contact API" -
                        were stamped on EVERY agent card in this grid. Identical
                        on every card, three lines each, naming three endpoints.
                        The real counts above them are what the card is for. */}
                  </button>
                );
              })}
            </div>
          )}
        </section>
      </div>

      <DetailDrawer
        open={selectedAgent !== null}
        onClose={() => setSelectedAgentId(null)}
        labelledBy={DRAWER_TITLE_ID}
        selectableSelector="button.wx-plc-agent-card"
      >
        {selectedAgent && (
          <AgentInspector agent={selectedAgent} plc={plc} superAgent={superAgent} titleId={DRAWER_TITLE_ID} />
        )}
      </DetailDrawer>
    </AppShell>
  );
}

function KpiCard({ label, value, reservedNote }) {
  return (
    <div className={`wx-sa-kpi${reservedNote ? " wx-sa-kpi--reserved" : ""}`}>
      <span className="wx-sa-kpi-label">{label}</span>
      {reservedNote ? (
        <span className="wx-sa-kpi-reserved">{reservedNote}</span>
      ) : (
        <strong className="wx-sa-kpi-value">{value}</strong>
      )}
    </div>
  );
}

function AgentInspector({ agent, plc, superAgent, titleId }) {
  return (
    <div className="wx-sa-inspector-body">
      <div className="wx-sa-inspector-head">
        <span className="wx-badge wx-badge--info">AG</span>
        <div>
          <h2 id={titleId} className="wx-sa-inspector-name">
            {agent.displayName}
          </h2>
          <span className="wx-sa-inspector-code">{agent.agentCode}</span>
        </div>
      </div>

      <p className="wx-state-body wx-loc-inspector-description">
        Agent ภายใต้ {plc.organizationName} · {superAgent.organizationName}
      </p>

      <div className="wx-kv-grid wx-sa-inspector-kv">
        <div>
          <div className="wx-kv-label">PLC / บมจ</div>
          <div className="wx-kv-value">{plc.organizationName}</div>
        </div>
        <div>
          <div className="wx-kv-label">Super Agent</div>
          <div className="wx-kv-value">{superAgent.organizationName}</div>
        </div>
        <div>
          <div className="wx-kv-label">แรงงานในความดูแล</div>
          <div className="wx-kv-value">{agent.workerCount ?? 0}</div>
        </div>
        <div>
          <div className="wx-kv-label">คำเชิญที่ส่งแล้ว</div>
          <div className="wx-kv-value">{agent.inviteCount ?? 0}</div>
        </div>
      </div>

      {/* A block of label + "รอเชื่อมต่อ <X> API" rows stood here - five
          labels naming five different endpoints, with no data under any of them.
          A panel whose entire content is a list of things we have not built. */}

      <Link
        className="wx-button wx-button--primary wx-sa-inspector-cta"
        to={`/backoffice/super-agents/${superAgent.organizationId}/plcs/${plc.organizationId}/agents/${agent.agentId}`}
      >
        เปิดหน้า Agent
      </Link>
    </div>
  );
}
